import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Search, Sparkles, Loader2, LifeBuoy, Trash2 } from "lucide-react";

const ROLE_FILTERS = [
  { key: "", label: "All" },
  { key: "customer", label: "Customers" },
  { key: "vendor", label: "Vendors" },
  { key: "farm_admin", label: "Farm admins" },
  { key: "farm_staff", label: "Staff" },
  { key: "ambassador", label: "Ambassadors" },
  { key: "admin", label: "Admin" },
];

export default function AdminUsersTab() {
  const [users, setUsers] = useState(null);
  const [role, setRole] = useState("");
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleting, setDeleting] = useState(false);

  const isProtected = (u) => ["admin", "super_admin"].includes(u.role);
  const toggle = (id) => setSelectedIds(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const load = () => api.get("/admin/users").then(r => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);

  const promote = async (u) => {
    if (!window.confirm(`Promote ${u.name || u.email} to Ambassador? This generates a referral code and Stripe promo automatically.`)) return;
    setBusyId(u.id);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/promote-ambassador`);
      setToast({ kind: "ok", msg: `${u.name || u.email} is now an Ambassador (code: ${data.referral_code || "—"})` });
      await load();
    } catch (e) {
      setToast({ kind: "err", msg: e.response?.data?.detail || "Promotion failed" });
    } finally {
      setBusyId(null);
      setTimeout(() => setToast(null), 4000);
    }
  };

  const filtered = (users || []).filter(u => {
    if (role && u.role !== role) return false;
    if (!q) return true;
    const needle = q.toLowerCase();
    return u.name?.toLowerCase().includes(needle) || u.email?.toLowerCase().includes(needle);
  });

  const selectAllFiltered = () => setSelectedIds(filtered.filter(u => !isProtected(u)).map(u => u.id));
  const selectExample = () => setSelectedIds(filtered.filter(u => !isProtected(u) && (u.email || "").toLowerCase().endsWith("@example.com")).map(u => u.id));
  const clearSel = () => setSelectedIds([]);

  const bulkDelete = async () => {
    const ids = selectedIds.filter(id => { const u = (users || []).find(x => x.id === id); return u && !isProtected(u); });
    if (!ids.length) return;
    if (!window.confirm(`Permanently delete ${ids.length} user(s)? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const { data } = await api.post("/admin/users/bulk-delete", { user_ids: ids });
      setToast({ kind: "ok", msg: `Deleted ${data.deleted} user(s).${data.skipped_protected ? ` Skipped ${data.skipped_protected} protected.` : ""}` });
      setSelectedIds([]);
      await load();
    } catch (e) {
      setToast({ kind: "err", msg: e.response?.data?.detail || "Delete failed" });
    } finally {
      setDeleting(false);
      setTimeout(() => setToast(null), 4000);
    }
  };

  if (users === null) return <div className="p-10 text-[var(--ff-text-muted)]" data-testid="admin-users-loading">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="admin-users-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-3xl">Users</h2>
          <p className="text-sm text-[var(--ff-text-muted)] mt-1">{filtered.length} of {users.length} users</p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ff-text-muted)]"/>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name or email" className="input-base pl-9 text-sm w-64" data-testid="admin-users-search"/>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="admin-users-role-filters">
        {ROLE_FILTERS.map(r => (
          <button key={r.key || "all"} onClick={() => setRole(r.key)} data-testid={`role-${r.key || "all"}`}
            className={`px-3 py-1.5 rounded-full border text-xs font-medium ${role === r.key ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]" : "bg-white border-[var(--ff-border)] text-[var(--ff-text-secondary)] hover:border-[var(--ff-primary)]"}`}>
            {r.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap card-flat p-3" data-testid="admin-users-bulk-bar">
        <span className="text-sm font-medium" data-testid="admin-users-selected-count">{selectedIds.length} selected</span>
        <button onClick={selectAllFiltered} className="btn-secondary text-xs" data-testid="admin-users-select-all">Select all shown</button>
        <button onClick={selectExample} className="btn-secondary text-xs" data-testid="admin-users-select-example">Select all @example.com</button>
        {selectedIds.length > 0 && <button onClick={clearSel} className="text-xs underline text-[var(--ff-text-muted)]" data-testid="admin-users-clear">Clear</button>}
        <button onClick={bulkDelete} disabled={deleting || selectedIds.length === 0}
          className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-rose-600 text-white px-4 py-1.5 text-xs font-medium disabled:opacity-40 hover:bg-rose-700 transition-colors"
          data-testid="admin-users-delete-selected">
          {deleting ? <Loader2 size={12} className="animate-spin"/> : <Trash2 size={12}/>} Delete selected
        </button>
      </div>

      <div className="card-flat divide-y divide-[var(--ff-border)]" data-testid="admin-users">
        {filtered.length === 0 && <p className="p-8 text-sm text-[var(--ff-text-muted)]">No users match.</p>}
        {filtered.map(u => {
          const canPromote = !["ambassador","admin","super_admin"].includes(u.role);
          const checked = selectedIds.includes(u.id);
          return (
            <div key={u.id} className={`p-4 flex items-center gap-4 ${checked ? "bg-rose-50/50" : ""}`} data-testid={`admin-user-${u.id}`}>
              <input type="checkbox" checked={checked} disabled={isProtected(u)} onChange={() => toggle(u.id)}
                className="w-4 h-4 shrink-0 accent-rose-600 disabled:opacity-30" data-testid={`admin-user-check-${u.id}`}
                title={isProtected(u) ? "Admin accounts can't be deleted" : "Select"}/>
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">{u.name}</p>
                <p className="text-xs text-[var(--ff-text-muted)] truncate">{u.email}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-xs uppercase tracking-wider text-[var(--ff-text-secondary)]">{u.role}</span>
                <Link
                  to={`/admin/users/${u.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[var(--ff-primary)] text-[var(--ff-primary)] hover:bg-[var(--ff-primary)] hover:text-white transition-colors px-3 py-1.5 text-xs font-medium"
                  data-testid={`support-view-${u.id}`}
                >
                  <LifeBuoy size={12} />
                  Edit / Support
                </Link>
                {canPromote && (
                  <button
                    onClick={() => promote(u)}
                    disabled={busyId === u.id}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[var(--ff-secondary)] text-[var(--ff-secondary)] hover:bg-[var(--ff-secondary)] hover:text-white transition-colors px-3 py-1.5 text-xs font-medium disabled:opacity-50"
                    data-testid={`promote-ambassador-${u.id}`}
                  >
                    {busyId === u.id ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                    Promote to Ambassador
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {toast && (
        <div
          className={`fixed bottom-6 right-6 max-w-sm rounded-xl border px-4 py-3 shadow-lg ${toast.kind === "ok" ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-rose-50 border-rose-200 text-rose-900"}`}
          data-testid="admin-users-toast"
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}
